<script lang="ts">
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import moment from "moment";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import { productContainerDetailsShort } from "@shared/filters/product_container_details.js";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  export let showPricingModal: boolean = false;
  export let product: Product;
  export let pricingForProduct;
  export let productLine: ProductLine;
  export let onCloseModal: () => void = () => {};

  const priceChanges = {};
  function setPriceChanges() {
    pricingForProduct.price_changes_by_tier.forEach(
      p => (priceChanges[p.future_price_id] = p.price_change),
    );
  }
  setPriceChanges();

  function getPriceChange(pricing) {
    const futurePriceId = pricing && pricing.future_price_id;
    if (!futurePriceId) return;

    const change = priceChanges[futurePriceId];
    if (!change) return;

    if (change === "up") {
      return "UpArrow";
    } else if (change === "down") {
      return "DownArrow";
    }
  }

  function getMinBottlePrice(pricesForMonth) {
    const containerCaseSize = product.container_case_size || 1;
    if (containerCaseSize <= 1) return;

    const casePrice = pricesForMonth.find(p => p.bottles === containerCaseSize);
    const bottlePrice = pricesForMonth.find(p => p.bottles === 1);

    if (casePrice && bottlePrice) return bottlePrice.price_per_unit;
  }

  function closeModal() {
    showPricingModal = false;
    onCloseModal();
  }

  let pastBottlePricingData = pricingForProduct.past_prices.find(p => p.bottles == 1);
  let currentBottlePricingData = pricingForProduct.current_prices.find(p => p.bottles == 1);
  let futureBottlePricingData = pricingForProduct.future_prices.find(p => p.bottles == 1);

  let pastBottlePrice = pastBottlePricingData?.price_per_unit;
  let currentBottlePrice = currentBottlePricingData?.price_per_unit;
  let futureBottlePrice = futureBottlePricingData?.price_per_unit;

  let pastCasePricingData =
    pricingForProduct.past_prices.find(p => p.bottles == product.container_case_size) ||
    pastBottlePricingData ||
    {};
  let currentCasePricingData =
    pricingForProduct.current_prices.find(p => p.bottles == product.container_case_size) ||
    currentBottlePricingData ||
    {};
  let futureCasePricingData =
    pricingForProduct.future_prices.find(p => p.bottles == product.container_case_size) ||
    futureBottlePricingData ||
    {};

  let pastCaseBasePrice = pastCasePricingData.list || pastCasePricingData.price_per_case;
  let currentCaseBasePrice = currentCasePricingData.list || currentCasePricingData.price_per_case;
  let futureCaseBasePrice = futureCasePricingData.list || futureCasePricingData.price_per_case;

  let pastCaseDiscount = parseFloat(pastCasePricingData.discount_per_case);
  let currentCaseDiscount = parseFloat(currentCasePricingData.discount_per_case);
  let futureCaseDiscount = parseFloat(futureCasePricingData.discount_per_case);

  let noFuturePricingData = pricingForProduct.future_prices.length === 0;

  const futureCasePriceChange = getPriceChange(futureCasePricingData);
  const futureBottlePriceChange = getPriceChange(futureBottlePricingData);

  const lastMonthMinBottlePrice = getMinBottlePrice(pricingForProduct.past_prices);
  const currentMonthMinBottlePrice = getMinBottlePrice(pricingForProduct.current_prices);
  const nextMonthMinBottlePrice = getMinBottlePrice(pricingForProduct.future_prices);

  const showMinBottle =
    lastMonthMinBottlePrice || currentMonthMinBottlePrice || nextMonthMinBottlePrice;
</script>

<Modal
  bind:show={showPricingModal}
  fizzModal={true}
  medium={true}
  overflow="auto"
  onClose={onCloseModal}
>
  <header class="fizz-modal-header">
    <h4 class="fizz-heading-4">Price Schedule</h4>
    <div class="fizz-modal-toggle fizz-modal-close" on:click={closeModal}>
      <Icon
        type="X"
        extraClassNames="fizz-text-brand cursor-pointer"
        height="16"
        width="16"
        id="close-future-prices"
      />
    </div>
  </header>
  <section class="fizz-modal-main fizz-text-medium">
    <div class="fizz-inset-square-16 fizz-bordered-squared fizz-surface-gray">
      <p class="fizz-text-strong">{productLine.name}</p>
      <p class="fizz-text-regular">
        {product.container_size}
        {product.container_units}
        {product.container_type}
      </p>
      <p class="fizz-text-regular">{productContainerDetailsShort(product)}</p>
    </div>
    <table
      class="fizz-table fizz-table-cell-borders fizz-table-condensed fizz-table-unresponsive"
      class:fizz-stack-8={noFuturePricingData}
    >
      <thead class="fizz-text-small fizz-align-right">
        <tr>
          <th />
          {#if !stagedRolloutCheck("marketplace__hide_past_prices")}
            <th width="30%" class="fizz-text-right fizz-text-strong fizz-text-subdued">
              <p>LAST</p>
              <p>{moment().subtract(1, "months").format("MMMM")}</p>
            </th>
          {/if}
          <th
            width="35%"
            class="fizz-surface-blue fizz-text-right fizz-text-strong fizz-text-subdued"
          >
            <p>CURRENT</p>
            <p>{moment().format("MMMM")}</p>
          </th>
          <th width="30%" class="fizz-text-right fizz-text-strong fizz-text-subdued">
            <p>NEXT</p>
            <p>{moment().add(1, "months").format("MMMM")}</p>
          </th>
        </tr>
      </thead>
      <tbody class="fizz-text-small">
        <tr>
          <td
            class="fizz-no-wrap fizz-align-right fizz-text-right fizz-text-strong fizz-text-subdued"
            >BUY FOR</td
          >
          {#if !stagedRolloutCheck("marketplace__hide_past_prices")}
            <td class="fizz-align-right fizz-text-right fizz-text-strong">
              {#if pastCasePricingData.price_per_case}
                <FormatMoney input={pastCasePricingData.price_per_case} formatType={"2"} />
              {:else}
                -
              {/if}
            </td>
          {/if}
          <td class="fizz-align-right fizz-text-right fizz-surface-blue fizz-text-strong">
            {#if currentCasePricingData.price_per_case}
              <FormatMoney input={currentCasePricingData.price_per_case} formatType={"2"} />
            {:else}
              -
            {/if}
          </td>
          <td class="fizz-align-right fizz-text-right fizz-text-strong">
            {#if futureCasePricingData.price_per_case}
              <FormatMoney input={futureCasePricingData.price_per_case} formatType={"2"} />
            {:else}
              -
            {/if}
            {#if futureCasePriceChange}
              <div class="fizz-inline-block">
                <Icon
                  type={futureCasePriceChange}
                  extraClassNames="fizz-text-brand"
                  id={`case-${futureCasePriceChange}-fp_id_${futureCasePricingData.future_price_id}`}
                />
              </div>
            {/if}
          </td>
        </tr>
        <tr>
          <td
            class="fizz-no-wrap fizz-align-right fizz-text-right fizz-text-strong fizz-text-subdued"
            >BASE PRICE</td
          >
          {#if !stagedRolloutCheck("marketplace__hide_past_prices")}
            <td class="fizz-align-right fizz-text-right">
              {#if pastCaseBasePrice}
                <p class="fizz-text-strong" class:strikethrough={pastCaseDiscount}>
                  <FormatMoney input={pastCaseBasePrice} formatType={"2"} />
                </p>
                {#if pastCaseDiscount}
                  <p class="fizz-text-subdued">
                    -<FormatMoney input={pastCasePricingData.discount_per_case} formatType={"2"} />
                  </p>
                {/if}
              {:else}
                -
              {/if}
            </td>
          {/if}
          <td class="fizz-align-right fizz-text-right fizz-surface-blue">
            {#if currentCaseBasePrice}
              <p class="fizz-text-strong" class:strikethrough={currentCaseDiscount}>
                <FormatMoney input={currentCaseBasePrice} formatType={"2"} />
              </p>
              {#if currentCaseDiscount}
                <p class="fizz-text-subdued">
                  -<FormatMoney input={currentCasePricingData.discount_per_case} formatType={"2"} />
                </p>
              {/if}
            {:else}
              -
            {/if}
          </td>
          <td class="fizz-align-right fizz-text-right">
            {#if futureCaseBasePrice}
              <p class="fizz-text-strong" class:strikethrough={futureCaseDiscount}>
                <FormatMoney input={futureCaseBasePrice} formatType={"2"} />
                {#if futureCasePriceChange}
                  <span class="fizz-inline-block">
                    <Icon
                      type={futureCasePriceChange}
                      extraClassNames="fizz-text-brand"
                      id={`base-${futureCasePriceChange}-fp_id_${futureCasePricingData.future_price_id}`}
                    />
                  </span>
                {/if}
              </p>
              {#if futureCaseDiscount}
                <p class="fizz-text-subdued">
                  -<FormatMoney input={futureCasePricingData.discount_per_case} formatType={"2"} />
                </p>
              {/if}
            {:else}
              -
            {/if}
          </td>
        </tr>
        <tr>
          <td
            class="fizz-no-wrap fizz-align-right fizz-text-right fizz-text-strong fizz-text-subdued"
            >COST/UNIT</td
          >
          {#if !stagedRolloutCheck("marketplace__hide_past_prices")}
            <td class="fizz-align-right fizz-text-right fizz-text-strong">
              {#if pastCasePricingData.price_per_unit}
                <FormatMoney input={pastCasePricingData.price_per_unit} formatType={"2"} />
              {:else}
                -
              {/if}
            </td>
          {/if}
          <td class="fizz-align-right fizz-text-right fizz-surface-blue fizz-text-strong">
            {#if currentCasePricingData.price_per_unit}
              <FormatMoney input={currentCasePricingData.price_per_unit} formatType={"2"} />
            {:else}
              -
            {/if}
          </td>
          <td class="fizz-align-right fizz-text-right fizz-text-strong">
            {#if futureCasePricingData.price_per_unit}
              <FormatMoney input={futureCasePricingData.price_per_unit} formatType={"2"} />
            {:else}
              -
            {/if}
            {#if futureCasePriceChange}
              <div class="fizz-inline-block">
                <Icon
                  type={futureCasePriceChange}
                  extraClassNames="fizz-text-brand"
                  id={`unit-${futureCasePriceChange}-fp_id_${futureCasePricingData.future_price_id}`}
                />
              </div>
            {/if}
          </td>
        </tr>
        {#if showMinBottle}
          <tr>
            <td
              class="fizz-no-wrap fizz-align-right fizz-text-right fizz-text-strong fizz-text-subdued"
              >MIN BOTTLE</td
            >
            {#if !stagedRolloutCheck("marketplace__hide_past_prices")}
              <td class="fizz-align-right fizz-text-right fizz-text-strong">
                {#if lastMonthMinBottlePrice}
                  <FormatMoney input={lastMonthMinBottlePrice} formatType={"2"} />
                {:else}
                  -
                {/if}
              </td>
            {/if}
            <td class="fizz-align-right fizz-text-right fizz-surface-blue fizz-text-strong">
              {#if currentMonthMinBottlePrice}
                <FormatMoney input={currentMonthMinBottlePrice} formatType={"2"} />
              {:else}
                -
              {/if}
            </td>
            <td class="fizz-align-right fizz-text-right fizz-text-strong">
              {#if nextMonthMinBottlePrice}
                <FormatMoney input={nextMonthMinBottlePrice} formatType={"2"} />
              {:else}
                -
              {/if}
              {#if futureBottlePriceChange}
                <div class="fizz-inline-block">
                  <Icon
                    type={futureBottlePriceChange}
                    extraClassNames="fizz-text-brand"
                    id={`bottle-${futureBottlePriceChange}-fp_id_${futureBottlePricingData.future_price_id}`}
                  />
                </div>
              {/if}
            </td>
          </tr>
        {/if}
      </tbody>
    </table>
    {#if pricingForProduct.future_prices.length === 0}
      <div class="fizz-text-subdued fizz-text-small">
        {productLine.distributor.name} has not confirmed pricing for next month. Please contact your
        rep for any questions.
      </div>
    {/if}
  </section>
</Modal>
